import { UserRole } from '../interfaces/user.interface';
import { FEATURE_KEYS, type FeatureKey } from '../constants/feature-keys';

/**
 * Sidebar navigation configuration for Pagai HR
 */

/**
 * Represents a navigation item in the sidebar
 */
export interface SidebarItem {
  /** Unique identifier for the menu item */
  readonly id: string;
  /** Display label shown in the sidebar */
  readonly label: string;
  /** Lucide icon name (e.g., 'lucideUsers') */
  readonly icon: string;
  /** Router path for navigation */
  readonly path: string;
  /** Roles that can see this item. If empty or undefined, visible to all. */
  readonly roles?: UserRole[];
  /**
   * Per-tenant feature flag gating this item. If set and the tenant has the
   * feature disabled, the item is hidden. If undefined, not feature-gated.
   */
  readonly feature?: FeatureKey;
}

/**
 * Main sidebar navigation items for HR module
 * Items without roles are visible to all authenticated users.
 * Items with roles are only visible to users with those specific roles.
 * Note: ADMIN has the same permissions as HR (they are equivalent roles).
 *
 * Order: cedolini, rimborsi, presenze e assenze, documenti, personale, aziende, impostazioni
 */
export const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    id: 'payslips',
    label: 'Cedolini',
    icon: 'lucideFileText',
    path: '/payslips',
    // Visible to all roles; hidden if the tenant has the payslips feature disabled
    feature: FEATURE_KEYS.PAYSLIPS,
  },
  {
    id: 'expense-reports',
    label: 'Rimborsi',
    icon: 'lucideReceipt',
    path: '/expense-reports',
    // Visible to all
  },
  {
    id: 'leave-requests',
    label: 'Presenze e Assenze',
    icon: 'lucideCalendarDays',
    path: '/leave-requests',
    // Visible to all
  },
  {
    id: 'documents',
    label: 'Documenti',
    icon: 'lucideFolderOpen',
    path: '/documents',
    // Visible to all
  },
  {
    id: 'employees',
    label: 'Personale',
    icon: 'lucideUsers',
    path: '/employees',
    // ADMIN, HR, and MANAGER can manage employees
    roles: [UserRole.ADMIN, UserRole.HR, UserRole.MANAGER],
  },
  {
    id: 'bacheca',
    label: 'Bacheca',
    icon: 'lucideMegaphone',
    path: '/bacheca',
    // Only ADMIN and HR can manage announcements
    roles: [UserRole.ADMIN, UserRole.HR],
  },
  {
    id: 'companies',
    label: 'Aziende',
    icon: 'lucideBuilding2',
    path: '/companies',
    // Only Labor Consultants can see the companies list
    roles: [UserRole.LABOR_CONSULTANT],
  },
];
